import { motion } from 'motion/react'
import { type ReactNode, Ref, useState } from 'react'

import { AstraParagraph } from '../Typography/AstraParagraph/AstraParagraph'
import { AstraSideNavContext, useSidenavContext } from './AstraSideNavContext'

export const SidebarDivider = () => <div className="mb-1 mt-2 h-px w-full bg-surface-backdrop" />

type SidebarSectionConfig = {
    title: string
    children: ReactNode
}

export const SidebarSection = ({ title, children }: SidebarSectionConfig) => {
    const isOpen = useSidenavContext()
    return (
        <div className="flex flex-col gap-1 pt-6">
            {isOpen ? <AstraParagraph className="truncate px-2.5 opacity-90">{title}</AstraParagraph> : <div className="h-5" />}

            {children}
        </div>
    )
}

type Props = {
    ref?: Ref<HTMLDivElement>
    isOpen: boolean
    children: ReactNode
}

/**
 * Sidebar is the application's primary navigation column. It animates between an expanded and a collapsed
 * rail via `isOpen`. Group links with `SidebarSection` and separate groups with `SidebarDivider`.
 *
 * @summary for the primary navigation column that collapses to a rail
 */
export const AstraSidebar = ({ ref, children, isOpen }: Props) => {
    const [renderOpen, setRenderOpen] = useState(isOpen)

    return (
        <motion.div
            ref={ref}
            data-test={`navigation-bar-${renderOpen ? 'open' : 'closed'}`}
            className="relative shrink-0 overflow-y-auto overflow-x-hidden"
            initial={false}
            animate={{ width: isOpen ? 220 : 40 }}
            transition={{ duration: 0.24, ease: 'easeInOut' }}
            onAnimationStart={() => {
                if (isOpen) setRenderOpen(true)
            }}
            onAnimationComplete={() => {
                if (!isOpen) setRenderOpen(false)
            }}
        >
            <AstraSideNavContext value={{ isOpen: renderOpen }}>
                <div className="flex h-full flex-col gap-y-2 py-2">{children}</div>
            </AstraSideNavContext>
        </motion.div>
    )
}
